import { alpha } from "@mui/material/styles";
import type React from "react";
import { type FC, useMemo } from "react";
import { INK, INK_BLACK, NEUTRAL } from "../../../../theme/tokens";

/**
 * A lightweight SVG loading skeleton that *looks like* a line/area chart.
 * - No Recharts dependency
 * - 3 placeholder series (one with area)
 * - Optional skeleton legend
 * - Fully responsive (fills its wrapper)
 */
type InHouseLineChartLoadingProps = {
	width?: React.CSSProperties["width"];
	height?: React.CSSProperties["height"];
	/** Show a pill-style skeleton legend above the plot */
	showLegendSkeleton?: boolean;
	/** Which series should look like an area (0..2) */
	areaIndex?: 0 | 1 | 2;
	/** For deterministic squiggles across renders */
	seed?: number;
	/** Axis & grid visibility */
	showAxes?: boolean;
	showGrid?: boolean;
	/** Aesthetic tweaks */
	cornerRadius?: number; // border radius for container
	background?: string; // container bg
	axisColor?: string;
	gridColor?: string;
	lineColor?: string; // base color for skeleton lines
	areaColor?: string; // base color for skeleton area
	shimmer?: boolean; // animated shimmer
	margin?: {
		top: number;
		right: number;
		bottom: number;
		left: number;
	};
};

const DEFAULTS = {
	width: "100%",
	height: "100%",
	axisColor: alpha(INK.SECONDARY, 0.5),
	gridColor: alpha(NEUTRAL[600], 0.15),
	lineColor: alpha(INK.SECONDARY, 0.45),
	areaColor: alpha(INK.SECONDARY, 0.2),
	background: "transparent",
};

/** Tiny xorshift PRNG for deterministic squiggles */
function makeRng(seed = 1337) {
	let s = seed >>> 0;
	return () => {
		s ^= s << 13;
		s ^= s >>> 17;
		s ^= s << 5;
		return (s >>> 0) / 0xffffffff;
	};
}

/** Build a soft random-walk path inside [padding..W-padding]x[padding..H-padding] */
function buildPath(W: number, H: number, rng: () => number, points = 24, padding = 24): { d: string; areaD: string } {
	const xs: number[] = [];
	const ys: number[] = [];

	const left = padding;
	const right = W - padding;
	const top = padding;
	const bottom = H - padding;

	for (let i = 0; i < points; i++) {
		xs.push(left + ((right - left) * i) / (points - 1));
	}

	// random walk centered vertically
	const mid = (top + bottom) / 2;
	let y = mid;
	for (let i = 0; i < points; i++) {
		const step = (rng() - 0.5) * (H * 0.09);
		y = Math.min(bottom, Math.max(top, y + step));
		ys.push(y);
	}

	// smooth path via cubic interpolation (simple Catmull-Rom-ish)
	const to = (i: number) => ({ x: xs[i], y: ys[i] });
	let d = `M ${to(0).x.toFixed(1)} ${to(0).y.toFixed(1)}`;
	for (let i = 1; i < points; i++) {
		const p0 = to(Math.max(0, i - 1));
		const p1 = to(i);
		const p2 = to(Math.min(points - 1, i + 1));
		const c1x = p0.x + (p1.x - to(Math.max(0, i - 2)).x) / 6;
		const c1y = p0.y + (p1.y - to(Math.max(0, i - 2)).y) / 6;
		const c2x = p1.x - (p2.x - p0.x) / 6;
		const c2y = p1.y - (p2.y - p0.y) / 6;
		d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p1.x.toFixed(1)} ${p1.y.toFixed(1)}`;
	}

	// area path closes to bottom
	const last = to(points - 1);
	const first = to(0);
	const areaD = `${d} L ${last.x.toFixed(1)} ${bottom} L ${first.x.toFixed(1)} ${bottom} Z`;
	return { d, areaD };
}

export const InHouseLineChartSkeleton: FC<InHouseLineChartLoadingProps> = ({
	width = DEFAULTS.width,
	height = DEFAULTS.height,
	showLegendSkeleton = false,
	areaIndex = 1,
	seed = 1337,
	showAxes = true,
	showGrid = true,
	cornerRadius = 12,
	background = DEFAULTS.background,
	axisColor = DEFAULTS.axisColor,
	gridColor = DEFAULTS.gridColor,
	lineColor = DEFAULTS.lineColor,
	areaColor = DEFAULTS.areaColor,
	shimmer = true,
	margin,
}) => {
	// SVG viewBox coordinates
	const VB_W = 1000;
	const VB_H = 360;

	const rng = useMemo(() => makeRng(seed), [seed]);
	const paths = useMemo(() => {
		return [0, 1, 2].map(() => buildPath(VB_W, VB_H, rng));
	}, [rng]);

	// Grid ticks (excluding borders so they don't collide with axes)
	const grid = useMemo(() => {
		const cols = 8;
		const rows = 4;
		const pad = 24;
		const left = pad;
		const right = VB_W - pad;
		const top = pad;
		const bottom = VB_H - pad;

		const vLines: number[] = [];
		for (let i = 1; i < cols; i++) {
			vLines.push(left + ((right - left) * i) / cols);
		}
		const hLines: number[] = [];
		for (let i = 1; i < rows; i++) {
			hLines.push(top + ((bottom - top) * i) / rows);
		}
		return { vLines, hLines, left, right, top, bottom };
	}, []);

	return (
		<div
			style={{
				width,
				height,
				borderRadius: cornerRadius,
				background,
				overflow: "hidden",
				marginTop: margin?.top,
				marginRight: margin?.right,
				marginBottom: margin?.bottom,
				marginLeft: margin?.left,
			}}
			aria-label="Loading chart skeleton"
			role="img"
		>
			<svg
				viewBox={`0 0 ${VB_W} ${VB_H}`}
				preserveAspectRatio="none"
				width="100%"
				height={showLegendSkeleton ? "calc(100% - 36px)" : "100%"}
				style={{ display: "block" }}
			>
				{/* defs for shimmer + gradient */}
				<defs>
					<linearGradient id="ih-line-shimmer" x1="0" x2="1" y1="0" y2="0">
						<stop offset="0%" stopColor={lineColor} stopOpacity="0.45" />
						<stop offset="50%" stopColor={lineColor} stopOpacity="0.75" />
						<stop offset="100%" stopColor={lineColor} stopOpacity="0.45" />
					</linearGradient>
					<linearGradient id="ih-area-fill" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor={areaColor} stopOpacity="0.9" />
						<stop offset="100%" stopColor={areaColor} stopOpacity="0.1" />
					</linearGradient>
					<clipPath id="ih-plot-clip">
						<rect x="12" y="8" width={VB_W - 24} height={VB_H - 16} rx="4" ry="4" />
					</clipPath>
				</defs>

				{/* Grid (no border-collision) */}
				{showGrid && (
					<g stroke={gridColor} strokeDasharray="2 2" clipPath="url(#ih-plot-clip)">
						{grid.vLines.map((x) => (
							<line key={`v-${x}`} x1={x} y1={grid.top} x2={x} y2={grid.bottom} />
						))}
						{grid.hLines.map((y) => (
							<line key={`h-${y}`} x1={grid.left} y1={y} x2={grid.right} y2={y} />
						))}
					</g>
				)}

				{/* Axes */}
				{showAxes && (
					<g stroke={axisColor}>
						{/* left axis */}
						<line x1={grid.left} y1={grid.top} x2={grid.left} y2={grid.bottom} />
						{/* bottom axis */}
						<line x1={grid.left} y1={grid.bottom} x2={grid.right} y2={grid.bottom} />
					</g>
				)}

				{/* Series (3 total; one as area) */}
				{paths.map((p, i) => {
					const isArea = i === areaIndex;
					const baseStrokeWidth = isArea ? 2 : 2;
					const dash = 8;
					const dashLen = 1400; // long dash for smooth sweeping
					const anim = shimmer ? <animate attributeName="stroke-dashoffset" from="0" to={dashLen} dur={`${40 + i * 0.3}s`} repeatCount="indefinite" /> : null;

					return (
						<g key={i} clipPath="url(#ih-plot-clip)">
							{isArea && (
								<path d={p.areaD} fill="url(#ih-area-fill)" opacity={0.8}>
									{shimmer && <animate attributeName="opacity" values="0.6;0.9;0.6" dur="20s" repeatCount="indefinite" />}
								</path>
							)}
							<path d={p.d} fill="none" stroke="url(#ih-line-shimmer)" strokeWidth={baseStrokeWidth} strokeDasharray={`${dash} ${dash}`} strokeLinecap="round">
								{anim}
							</path>
						</g>
					);
				})}
			</svg>
			{showLegendSkeleton && (
				<div
					style={{
						display: "flex",
						gap: 12,
						padding: "0px 12px 0 12px",
						flexWrap: "wrap",
					}}
				>
					{["Series A", "Series B", "Series C"].map((label, i) => (
						<div
							key={label}
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: 8,
								padding: "6px 10px",
								borderRadius: 999,
								background: alpha(INK_BLACK, 0.05),
							}}
						>
							<span
								style={{
									width: 10,
									height: 10,
									borderRadius: "50%",
									background: lineColor,
									opacity: 0.7,
								}}
							/>
							<span
								style={{
									width: 68,
									height: 10,
									borderRadius: 6,
									background: `linear-gradient(90deg, ${alpha(INK_BLACK, 0.08)} 25%, ${alpha(INK_BLACK, 0.14)} 37%, ${alpha(INK_BLACK, 0.08)} 63%)`,
									backgroundSize: "200% 100%",
									animation: shimmer ? "ih-skel 3s ease-in-out infinite" : "none",
								}}
							/>
						</div>
					))}
				</div>
			)}
			{/* local keyframes for legend shimmer */}
			<style>
				{`
          @keyframes ih-skel {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
			</style>
		</div>
	);
};
